import axios from 'axios';

const ODX_ADMIN_BASE_URL = process.env.REACT_APP_ODX_ADMIN_BASE_URL;
const ACCESS_TOKEN = process.env.REACT_APP_ODX_ADMIN_TOKEN;

const odkAdminApiService = {
  // /admin/api/content_providers/?ordering=code&service_name=odc
  fetchCpFromOdc: async (): Promise<any[]> => {
    try {
      const response = await axios.get(
        `${ODX_ADMIN_BASE_URL}/admin/api/content_providers/?ordering=code&service_name=odc`,
        {
          headers: {
            Authorization: `Token ${ACCESS_TOKEN}`,
            'Content-Type': 'application/json',
            'Service-Name': 'odc',
          },
        }
      );
      return response.data.results.map((item: any) => ({
        key: item.code,
        label: item.name,
        value: item.code,
      }));
    } catch (e) {
      console.warn('fetchCpFromOdc failed (ODX Admin unavailable)');
      return [];
    }
  },

  fetchCpFromOdk: async (): Promise<any[]> => {
    try {
      const response = await axios.get(
        `${ODX_ADMIN_BASE_URL}/admin/api/legacy/programs_cp_list/?service_name=odk`,
        {
          headers: {
            Authorization: `Token ${ACCESS_TOKEN}`,
            'Content-Type': 'application/json',
            'Service-Name': 'odk',
          },
        }
      );
      return response.data.cp_list
        .map((item: any) => ({
          key: item,
          label: item,
          value: item,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
    } catch (e) {
      console.warn('fetchCpFromOdk failed (ODX Admin unavailable)');
      return [];
    }
  },

  fetchOdkProgram: async (id: number): Promise<{ enable: boolean } & Record<string, any>> => {
    const response = await axios.get(`${ODX_ADMIN_BASE_URL}/admin/api/programs/${id}/`, {
      headers: {
        Authorization: `Token ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json',
        'Service-Name': 'odk',
      },
    });
    return response.data;
  },

  fetchOdkEpisode: async (id: number): Promise<{ enable: boolean } & Record<string, any>> => {
    const response = await axios.get(`${ODX_ADMIN_BASE_URL}/admin/api/episodes/${id}/`, {
      headers: {
        Authorization: `Token ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json',
        'Service-Name': 'odk',
      },
    });
    return response.data;
  },

  fetchAmasianSeason: async (id: number): Promise<{ status: string } & Record<string, any>> => {
    const response = await axios.get(
      `${ODX_ADMIN_BASE_URL}/odx/api/v4/amasian/admin/season/${id}/`,
      {
        headers: {
          Authorization: `Token ${ACCESS_TOKEN}`,
          'Content-Type': 'application/json',
          'Service-Name': 'amasian',
        },
      }
    );
    return response.data;
  },

  fetchAmasianContent: async (id: number): Promise<{ status: string } & Record<string, any>> => {
    const response = await axios.get(
      `${ODX_ADMIN_BASE_URL}/odx/api/v4/amasian/admin/content/${id}/`,
      {
        headers: {
          Authorization: `Token ${ACCESS_TOKEN}`,
          'Content-Type': 'application/json',
          'Service-Name': 'amasian',
        },
      }
    );
    return response.data;
  },

  /**
   * BEE-5023 PRD v1.4 #19 (LIVE flow): subtype='live' + cta_type='internal' 일 때
   * 운영자가 channel id 를 raw 입력하지 않도록 서비스(ODK / Amasian) admin API
   * 에서 채널 목록을 가져와 picker 로 노출. 결과는 antd Select 형식으로 정규화.
   */
  fetchOdkChannels: async (): Promise<{ value: number; label: string }[]> => {
    try {
      const response = await axios.get(
        `${ODX_ADMIN_BASE_URL}/admin/api/channel/`,
        {
          headers: {
            Authorization: `Token ${ACCESS_TOKEN}`,
            'Content-Type': 'application/json',
            'Service-Name': 'odk',
          },
        }
      );
      const results = response.data.results ?? response.data ?? [];
      return results
        .map((c: any) => ({
          value: Number(c.id),
          label: `${c.name ?? c.title ?? `Channel #${c.id}`} (id: ${c.id})`,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
    } catch (e) {
      console.warn('fetchOdkChannels failed (ODX Admin unavailable)');
      return [];
    }
  },

  fetchAmasianChannels: async (): Promise<{ value: number; label: string }[]> => {
    try {
      const response = await axios.get(
        `${ODX_ADMIN_BASE_URL}/odx/api/v4/amasian/admin/multi-channel/`,
        {
          headers: {
            Authorization: `Token ${ACCESS_TOKEN}`,
            'Content-Type': 'application/json',
            'Service-Name': 'amasian',
          },
        }
      );
      const results = response.data.results ?? response.data ?? [];
      return results
        .map((c: any) => ({
          value: Number(c.id),
          label: `${c.name ?? c.title ?? `Channel #${c.id}`} (id: ${c.id})`,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
    } catch (e) {
      console.warn('fetchAmasianChannels failed (ODX Admin unavailable)');
      return [];
    }
  },

  // ODC 검색 API (검색할 때마다 호출)
  searchCpFromOdc: async (searchTerm: string = ''): Promise<any[]> => {
    try {
      const url = searchTerm.trim()
        ? `${ODX_ADMIN_BASE_URL}/admin/api/content_providers/?search=${encodeURIComponent(searchTerm)}&ordering=code&service_name=odc`
        : `${ODX_ADMIN_BASE_URL}/admin/api/content_providers/?ordering=code&service_name=odc`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Token ${ACCESS_TOKEN}`,
          'Content-Type': 'application/json',
          'Service-Name': 'odc',
        },
      });
      return response.data.results.map((item: any) => ({
        key: item.code,
        label: item.name,
        value: item.code,
      }));
    } catch (e) {
      console.warn('searchCpFromOdc failed (ODX Admin unavailable)');
      return [];
    }
  },
};

export default odkAdminApiService;
